<!-- THIS IS PART OF FindCar.vue -->
<template>
  <div class="flex flex-col gap-2 find-your-car-list">
    <PanelTitle :title="resultsStepTitle" :subTitle="resultsStepContent" />
    <RadioCardList
      v-model="rbcSelectedVehicleOption"
      :bindings="{
        options: rbcVehicleOptions,
        optionsClass: 'racq-max-w-xl',
        validation: [['required']],
        validationMessages: { required: 'Please select an option' },
      }"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { IFoundVehicle } from '@/interfaces';
import useVehicleData from '@/composables/useVehicleData';
import { clone, uniqBy } from 'lodash-es';
import { IRadioButtonCard } from '@/interfaces/entities/radio-button-card.interface';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import RadioCardList from '@/components/RadioCardList/RadioCardList.vue';

// Define Props & Emitters
// ------------------------------------------------------------- //
const props = defineProps<{
  vehicleList: IFoundVehicle[];
  heading?: string;
  foundVehiclesByRego?: boolean;
  regoResultsStepTitle?: string;
  regoResultsStepContent?: string;
  makeAndModelResultsStepTitle?: string;
  makeAndModelResultsStepContent?: string;
  showReleaseDate?: boolean;
}>();

const emit = defineEmits(['vehicleSelected']);
const vehicleData = useVehicleData();
const vehicleDescriptionMode = ref('basic');
const uniqVehicleList = ref<IFoundVehicle[]>([]);

const carImage = { value: { src: require('@/assets/icons/car.svg'), alt: 'Car Icon' } };

// Define Component state
// ------------------------------------------------------------- //
const selectedVehicle = ref<IFoundVehicle | undefined>(undefined);

const makeModelDescriptionRego = computed(
  () => (vehicle: IFoundVehicle) =>
    `${vehicle.make.description} ${vehicle.model.description} ${vehicle.registrationNumber?.toUpperCase() ?? ''}`.trim()
);

const resultsStepTitle = computed(() => {
  return {
    value: props.foundVehiclesByRego ? props.regoResultsStepTitle : props.makeAndModelResultsStepTitle,
  };
});

const resultsStepContent = computed(() => {
  return {
    value: props.foundVehiclesByRego ? props.regoResultsStepContent : props.makeAndModelResultsStepContent,
  };
});

const rbcVehicleOptions = ref<IRadioButtonCard[]>([]);
const rbcSelectedVehicleOption = ref('');

function getDetailedDescription(vehicle: IFoundVehicle): string {
  return vehicleData.formattedCarSpecs(vehicle, 'detailed');
}

function shouldShowReleaseDate(vehicle: IFoundVehicle): boolean {
  if (!props.showReleaseDate || vehicleDescriptionMode.value !== 'detailed') return false;

  const detailedDescription = getDetailedDescription(vehicle);
  const matchingVehicles = props.vehicleList.filter(
    (candidate) => getDetailedDescription(candidate) === detailedDescription
  );

  if (matchingVehicles.length <= 1 || !vehicle.releaseDate) return false;

  const uniqueReleaseDates = [...new Set(matchingVehicles.map((candidate) => candidate.releaseDate ?? ''))];
  return uniqueReleaseDates.length > 1;
}

// Define Lifycycle hooks
// ------------------------------------------------------------- //
onMounted(() => {
  uniqVehicleList.value = clone(props.vehicleList);
  vehicleDescriptionMode.value = 'basic';

  if (props.vehicleList?.length === 1) {
    rbcSelectedVehicleOption.value = props.vehicleList[0].glassesId?.toString();
  } else {
    const basicDescriptions = props.vehicleList.map((v) => vehicleData.formattedCarSpecs(v, 'basic'));
    const hasDuplicatedBasicDescription = basicDescriptions.some((desc, i) => basicDescriptions.indexOf(desc) !== i);

    if (hasDuplicatedBasicDescription) {
      vehicleDescriptionMode.value = 'detailed';

      if (!props.showReleaseDate) {
        uniqVehicleList.value = uniqBy(props.vehicleList, (vehicle) =>
          vehicleData.formattedCarSpecs(vehicle, 'detailed')
        );
      } else {
        const duplicateGroups = new Map<string, IFoundVehicle[]>();

        props.vehicleList.forEach((vehicle) => {
          const key = getDetailedDescription(vehicle);
          const group = duplicateGroups.get(key) ?? [];
          group.push(vehicle);
          duplicateGroups.set(key, group);
        });

        const shouldCollapseGroup = (group: IFoundVehicle[]) => {
          if (group.length <= 1) return false;
          const dates = [...new Set(group.map((vehicle) => vehicle.releaseDate ?? ''))];
          return dates.length === 1;
        };

        uniqVehicleList.value = props.vehicleList.filter((vehicle) => {
          const group = duplicateGroups.get(getDetailedDescription(vehicle));
          if (!group || group.length <= 1) return true;
          if (shouldCollapseGroup(group)) {
            return group[0] === vehicle;
          }
          return true;
        });
      }
    }
  }

  rbcVehicleOptions.value = setRadioButtonCardOptions(uniqVehicleList.value);
});

// Watch for changes to update parent modelValue
watch(rbcSelectedVehicleOption, (newValue) => {
  const selectedVehicle = uniqVehicleList.value.find((uv) => uv.glassesId.toString() === newValue);
  selectVehicle(selectedVehicle as IFoundVehicle);
});

function setRadioButtonCardOptions(uniqueVehicles: IFoundVehicle[]): any[] {
  return uniqueVehicles.map((vehicle) => {
    const description = vehicleData.formattedCarSpecs(vehicle, vehicleDescriptionMode.value);

    const help =
      shouldShowReleaseDate(vehicle) && vehicle.releaseDate
        ? `${description} - Release Date ${vehicle.releaseDate}`
        : description;

    return {
      value: vehicle.glassesId.toString(),
      image: carImage,
      label: makeModelDescriptionRego.value(vehicle),
      help,
    };
  });
}

// Define Functions
// ------------------------------------------------------------- //
function selectVehicle(vehicle: IFoundVehicle) {
  selectedVehicle.value = vehicle;
  emit('vehicleSelected', vehicle);
}
</script>
